import { computed, Ref } from '@nuxtjs/composition-api';
import { User } from '@vsf-enterprise/sap-commerce-webservices-sdk';
import { Logger, sharedRef, useVSFContext } from '@vue-storefront/core';
import { ChangeUserPasswordProps, LoadUserProps, LoginUserProps, RegisterUserProps, UpdateUserProps, UseUserComposable, UseUserError } from '../types';
import * as actions from './actions';

export const useUser = (): UseUserComposable => {
  const context = useVSFContext();
  const user: Ref<User> = sharedRef(null, 'useUser-user');
  const loading: Ref<boolean> = sharedRef(false, 'useUser-loading');
  const error: Ref<UseUserError> = sharedRef({
    load: null,
    login: null,
    logout: null,
    register: null,
    update: null,
    changePassword: null
  }, 'useUser-error');

  const methodController = async (
    methodName: string,
    callback: () => Promise<void>
  ) => {
    Logger.debug(`useUser.${methodName}`);

    try {
      loading.value = true;
      await callback();
      error.value[methodName] = null;
    } catch (err) {
      error.value[methodName] = err;
      Logger.error(`useUser.${methodName}`, err);
    } finally {
      loading.value = false;
    }
  };

  const load = async (props?: LoadUserProps) => {
    await methodController('load', async () => {
      user.value = await actions.loadUser(context, props || {});
    });
  };

  const login = async (props: LoginUserProps) => {
    await methodController('login', async () => {
      await actions.login(context, props);
      user.value = await actions.loadUser(context, props);
    });
  };

  const logout = async () => {
    await methodController('logout', async () => {
      await actions.logout(context);
      user.value = null;
    });
  };

  const register = async (props: RegisterUserProps) => {
    await methodController('register', async () => {
      await actions.registerUser(context, props);
    });
  };

  const update = async (props: UpdateUserProps) => {
    await methodController('update', async () => {
      user.value = await actions.updateUser(context, props);
    });
  };

  const changePassword = async (props: ChangeUserPasswordProps) => {
    await methodController('changePassword', async () => {
      await actions.changeUserPassword(context, props);
    });
  };

  const set = (newUser: User | null) => {
    user.value = newUser;
  };

  return {
    user: computed(() => user.value),
    isAuthenticated: computed(() => Boolean(user.value)),
    loading: computed(() => loading.value),
    error: computed(() => error.value),
    load,
    login,
    logout,
    register,
    update,
    changePassword,
    set
  };
};
